import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Entitlements mapped across every app',
    description:
      'One model for the access a user actually has (roles, groups, fine-grained permissions) collapsed across every connected system.',
    bullets: [
      'Cross-app entitlement normalization',
      'Group, role, and fine-grained permission types',
      'Per-app and aggregated views',
      'Drift detection between source and target',
    ],
  },
  {
    title: 'Access reviews that finish',
    description:
      'Schedule reviews by risk tier, app owner, or manager. Bulk-approve, diff-highlight, and auto-revoke unattested access.',
    bullets: [
      'Manager- and app-owner-driven campaigns',
      'Risk tier prioritization',
      'Diff highlighting on recent changes',
      'Auto-revoke on overdue attestation',
    ],
  },
  {
    title: 'Separation of duties, enforced',
    description:
      'Encode SoD rules as policies. Monosync alerts on violations at request time and during reviews, before they become audit findings.',
    bullets: [
      'Configurable SoD rule sets',
      'Real-time violation alerts on access grants',
      'Audit-mode for legacy environments',
      'Exception workflow with compensating controls',
    ],
  },
  {
    title: 'Audit-ready reports, exportable',
    description:
      'Pre-built reports for SOC 2, ISO 27001, GDPR, KVKK, and PCI-DSS. Exportable in PDF, CSV, and signed JSON for chain-of-custody.',
    bullets: [
      'Framework-aligned report templates',
      'Custom report builder',
      'Signed exports with tamper detection',
      'API access for SIEM and GRC tools',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Map the entitlements',
    description:
      'Monosync pulls entitlements from every connected system and normalizes them into one model.',
  },
  {
    title: 'Schedule reviews',
    description:
      'Set cadences by risk tier and ownership. Reviewers see only what they own; no spreadsheet bloat.',
  },
  {
    title: 'Prove it',
    description:
      'Export framework-aligned reports straight to your auditor, with signed chain-of-custody intact.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Monosync API',
    description:
      'Programmatic access to the entitlement graph.',
    href: '/sync/api',
  },
  {
    label: 'User lifecycle',
    description:
      'Lifecycle workflows that feed the governance layer.',
    href: '/sign/lifecycle',
  },
  {
    label: 'Audit & compliance',
    description:
      'Use case: making auditors happy without slowing engineering down.',
    href: '/solutions/audit-compliance',
  },
]

export default function GovernancePage() {
  return (
    <Layout>
      <SolutionMeta
        title="Identity governance | Monosync"
        description="Cross-app entitlement mapping, scheduled access reviews, SoD enforcement, and audit-ready reports for SOC 2, ISO 27001, GDPR, KVKK, and PCI-DSS."
        path="/sync/governance"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monosync · Governance"
          title={
            <>
              Who has access?
              <br />
              Prove it on demand.
            </>
          }
          body="Entitlements mapped across every connected system, access reviews that actually finish, separation-of-duties policies enforced at request time, and reports your auditors can take to the board."
          badges={[
            'Cross-app entitlement model',
            'SoD enforcement',
            'Audit-ready reports',
          ]}
        />
        <SolutionPillars
          title={<>Governance is a discipline, not a quarterly fire drill.</>}
          body="Monosync turns access governance from a spreadsheet panic into a steady-state operation: visible, reviewed, and provable on any day of the year."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Map. Review. Prove.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
